import { Component, Input, OnDestroy, OnInit, input, inject } from '@angular/core';
import { FormControl, FormGroup, UntypedFormControl, ReactiveFormsModule } from '@angular/forms';
import { Structure } from '../../core/models/structure.model';
import { Observable, Subject, combineLatest } from 'rxjs';
import { debounceTime, map, startWith, takeUntil } from 'rxjs/operators';
import { StructureValidator } from '../base-form/validators-config';
import { StructureService } from '../structure/structure.service';
import { MatFormField, MatLabel, MatSuffix, MatError } from '@angular/material/form-field';
import { NgStyle, AsyncPipe } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatIcon } from '@angular/material/icon';
import { MatOption } from '@angular/material/select';

type NomDeChampDeStructureDeTypeString =
    | {
          [P in keyof Structure]: Structure[P] extends string | null ? P : never;
      }[keyof Structure]
    | 'codeUai'; // TODO corriger ce type

@Component({
    selector: 'app-select-structure',
    templateUrl: './select-structure.component.html',
    styleUrls: ['./select-structure.component.scss'],
    imports: [
        MatFormField,
        ReactiveFormsModule,
        NgStyle,
        MatLabel,
        MatInput,
        MatAutocompleteTrigger,
        MatIcon,
        MatSuffix,
        MatError,
        MatAutocomplete,
        MatOption,
        AsyncPipe
    ]
})
export class SelectStructureComponent implements OnInit, OnDestroy {
    private service = inject(StructureService);
    private asyncStructureValidator = inject(StructureValidator);

    @Input({ required: true }) name: string;
    readonly label = input.required<string>();
    @Input({ required: true }) parent: FormGroup;
    readonly keyOfStructureToSave = input<NomDeChampDeStructureDeTypeString>('code');
    readonly formFieldStyles = input<{
        [klass: string]: unknown;
    } | null>();
    readonly useActualSpaceValidityDate = input<Date | null>(null);
    structureFormControl: FormControl;
    filteredStructures$: Observable<Structure[]>;

    private destroyed$: Subject<void> = new Subject<void>();

    ngOnInit(): void {
        this.structureFormControl = this.parent.controls[this.name] as UntypedFormControl;
        this.structureFormControl.addAsyncValidators(
            this.asyncStructureValidator
                .validateWithKey(this.keyOfStructureToSave(), this.useActualSpaceValidityDate() ?? null)
                .bind(this.asyncStructureValidator)
        );
        if (this.structureFormControl.value) {
            this.structureFormControl.markAsTouched(); // Ne pas trigger si le formulaire est vide
        }

        const obserVableStructures$ = this.service.listStructures().pipe(
            map(structures =>
                structures
                    .filter(s => s.codeUai != null)
                    .filter(s => {
                        const useActualSpaceValidityDate = this.useActualSpaceValidityDate();
                        return !useActualSpaceValidityDate || s.isValidAtDate(useActualSpaceValidityDate);
                    })
            )
        );

        this.filteredStructures$ = combineLatest([
            obserVableStructures$,
            this.structureFormControl.valueChanges.pipe(
                startWith(''),
                debounceTime(200),
                map((text: string) => text?.toUpperCase() ?? '')
            )
        ]).pipe(
            map(([structures, recherche]: [Structure[], string]) => {
                return structures.filter(
                    s => (!!s && (s[this.keyOfStructureToSave()] as string)?.startsWith(recherche)) || s.libelle.startsWith(recherche)
                );
            }),
            takeUntil(this.destroyed$)
        );
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    reset(event: MouseEvent) {
        event.stopImmediatePropagation();
        this.parent.get(this.name)?.reset(null);
        this.parent.get(this.name)?.markAsDirty();
    }

    getFullValue(structures: Structure[]) {
        return (value: string) => {
            if (value) {
                if (structures.find(st => st[this.keyOfStructureToSave()] === value)) {
                    const selectedValue = structures.find(st => st[this.keyOfStructureToSave()] === value);
                    return `${selectedValue![this.keyOfStructureToSave()]} - ${selectedValue?.libelle}`;
                } else {
                    return value;
                }
            } else {
                return '';
            }
        };
    }
}
